import { LoginPage } from 'features/auth';
import OAuthCallbackPage from 'features/auth/components/OAuthCallbackPage';
import { ClientDetailPage, ClientProjectDetailPage, ClientsPage } from 'features/clients';
import { DashboardPage } from 'features/dashboard';
import { ProjectDetailPage, ProjectsPage } from 'features/projects';
import { ReportsPage, SmartReportPage } from 'features/reports';
import { SettingsPage } from 'features/settings';
import { TeamPage } from 'features/team';
import { TrackerPage } from 'features/timer';
import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from 'shared/components/AppLayout';
import { LoadingOverlay } from 'shared/components/LoadingOverlay';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { ProtectedRoute } from 'shared/guards/ProtectedRoute';
import { useAppConfig } from 'shared/hooks/useAppConfig';
import { usePermissions } from 'shared/hooks/usePermissions';

const ClientsRoute: React.FC = () => {
  const { canViewClients } = usePermissions();
  return canViewClients ? <ClientsPage /> : <Navigate to={ROUTE_PATHS.TRACKER} replace />;
};

const ClientDetailRoute: React.FC = () => {
  const { canViewClients } = usePermissions();
  return canViewClients ? <ClientDetailPage /> : <Navigate to={ROUTE_PATHS.TRACKER} replace />;
};

const ClientProjectDetailRoute: React.FC = () => {
  const { canViewClients } = usePermissions();
  return canViewClients ? <ClientProjectDetailPage /> : <Navigate to={ROUTE_PATHS.TRACKER} replace />;
};

const SmartReportRoute: React.FC = () => {
  const { smartReportEnabled } = useAppConfig();
  return smartReportEnabled ? <SmartReportPage /> : <Navigate to={ROUTE_PATHS.REPORTS} replace />;
};

export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingOverlay />}>
      <Routes>
        {/* Public routes */}
        <Route path={ROUTE_PATHS.LOGIN} element={<LoginPage />} />
        <Route path={ROUTE_PATHS.AUTH_CALLBACK} element={<OAuthCallbackPage />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path={ROUTE_PATHS.DASHBOARD} element={<DashboardPage />} />
            <Route path={ROUTE_PATHS.TRACKER} element={<TrackerPage />} />
            <Route path={ROUTE_PATHS.PROJECTS} element={<ProjectsPage />} />
            <Route path={`${ROUTE_PATHS.PROJECTS}/:id`} element={<ProjectDetailPage />} />
            <Route path={ROUTE_PATHS.CLIENTS} element={<ClientsRoute />} />
            <Route path={`${ROUTE_PATHS.CLIENTS}/:id`} element={<ClientDetailRoute />} />
            <Route
              path={`${ROUTE_PATHS.CLIENTS}/:clientId/projects/:projectId`}
              element={<ClientProjectDetailRoute />}
            />
            <Route path={ROUTE_PATHS.SMART_REPORT} element={<SmartReportRoute />} />
            <Route path={ROUTE_PATHS.REPORTS} element={<ReportsPage />} />
            <Route path={ROUTE_PATHS.TEAM} element={<TeamPage />} />
            <Route path={ROUTE_PATHS.SETTINGS} element={<SettingsPage />} />
          </Route>
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to={ROUTE_PATHS.TRACKER} replace />} />
        <Route path="*" element={<Navigate to={ROUTE_PATHS.LOGIN} replace />} />
      </Routes>
    </Suspense>
  );
};
